import React from 'react';
import { Snackbar, Paper, Typography, IconButton } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { CheckCircle, Error, Warning, Info, Close } from '@mui/icons-material';

const SnackbarContent = styled(Paper)({
    borderRadius: '6px',
    boxShadow: '0px 2px 4px -1px rgba(10, 9, 11, 0.02), 0px 5px 13px -5px rgba(10, 9, 11, 0.05)',
    padding: '16px 20px',
    minWidth: 'auto',
    maxWidth: 'calc(100vw - 64px)',
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    position: 'relative',
    overflow: 'hidden',
});

const TopBar = styled('div')({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    transition: 'background 0.5s ease',
});

const IconWrapper = styled('div')({
    marginRight: '16px',
    display: 'flex',
    alignItems: 'center',
});

const Message = styled(Typography)(({ theme }) => ({
    flex: 0,
    color: theme.palette.text.primary,
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: 1.5,
    whiteSpace: 'nowrap',
    '@media (max-width: 600px)': {
        whiteSpace: 'normal',
    },
}));

const CloseButton = styled(IconButton)({
    marginLeft: '12px',
    padding: '6px',
    '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
});

interface EnhancedSnackbarProps {
    open?: boolean;
    message?: React.ReactNode;
    severity?: 'success' | 'error' | 'warning' | 'info';
    onClose?: () => void;
    autoHideDuration?: number;
    [key: string]: any;
}

function EnhancedSnackbar(props: EnhancedSnackbarProps) {
    const { open, message, severity = 'success', onClose, autoHideDuration = 4000 } = props;
    const theme = useTheme();

    const handleClose = (event, reason?) => {
        if (reason === 'clickaway') {
            return;
        }
        if (onClose) {
            onClose();
        }
    };

    const getSeverityColor = () => {
        switch (severity) {
            case 'success':
                return '#22b36e';
            case 'error':
                return theme.palette.error.main;
            case 'warning':
                return theme.palette.warning?.main || '#ff9800';
            case 'info':
                return theme.palette.info?.main || '#2196f3';
            default:
                return '#22b36e';
        }
    };

    const getIcon = () => {
        const iconStyle = { fontSize: '28px', color: getSeverityColor() };
        switch (severity) {
            case 'success':
                return <CheckCircle style={iconStyle} />;
            case 'error':
                return <Error style={iconStyle} />;
            case 'warning':
                return <Warning style={iconStyle} />;
            case 'info':
                return <Info style={iconStyle} />;
            default:
                return <CheckCircle style={iconStyle} />;
        }
    };

    return (
        <Snackbar
            open={open}
            autoHideDuration={autoHideDuration}
            onClose={handleClose}
            anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
            }}
            style={{ top: '24px' }}
        >
            <SnackbarContent>
                <TopBar style={{ backgroundColor: getSeverityColor() }} />
                <IconWrapper>
                    {getIcon()}
                </IconWrapper>
                <Message>
                    {message}
                </Message>
                <CloseButton
                    size="small"
                    aria-label="close"
                    color="inherit"
                    onClick={handleClose}
                >
                    <Close fontSize="small" style={{ color: theme.palette.text.secondary }} />
                </CloseButton>
            </SnackbarContent>
        </Snackbar>
    );
}

export default EnhancedSnackbar;
